// Topbar "Report a bug" trigger — icon-only button rendered inline in the
// topbar, immediately to the left of the theme toggle. Click opens
// BugReportModal. Keep this component lean — the modal (and its
// modern-screenshot import) only loads when the user clicks.
//
// Visibility policy must match the backend gate in services/api/src/routes/
// bug-reports.ts: fastn email (@fastn.ai or @fastn.dev) AND role in
// {platform_admin, admin, developer}. Without the role check, pending-
// membership users (default role end_user) saw the button but the modal
// was a dead end — both /assignees and the submit POST returned 403, so
// the dropdown rendered as "Unassigned (couldn't load list)".

import * as React from "react";
import { Bug } from "lucide-react";
import { BugReportModal } from "./BugReportModal";
import { useAuth } from "@/lib/auth";
import { bugReportTestIds } from "@/testids/bugReportTestIds";

const FASTN_EMAIL_RE = /@fastn\.(ai|dev)$/i;
const ALLOWED_ROLES = new Set(["platform_admin", "owner", "admin", "developer"]);

// Temporarily disabled — the assignee endpoint fails for pending-membership
// users and the modal renders as a dead end. Flip back to false to re-enable
// once the /assignees + submit flow is stable.
const BUG_REPORT_HIDDEN = true;

export function BugReportButton(): React.ReactElement | null {
  const [open, setOpen] = React.useState(false);
  const { userContext } = useAuth();

  if (BUG_REPORT_HIDDEN) return null;
  if (!userContext) return null;
  if (!FASTN_EMAIL_RE.test(userContext.email ?? "")) return null;
  if (!ALLOWED_ROLES.has(userContext.role)) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={bugReportTestIds.triggerButton}
        aria-label="Report a bug"
        title="Report a bug"
        className="fx-btn fx-btn--ghost fx-btn--icon fx-btn--sm"
      >
        <Bug size={16} strokeWidth={1.5} aria-hidden />
      </button>

      {open && <BugReportModal onClose={() => setOpen(false)} />}
    </>
  );
}
